import React, { useEffect, useMemo, useState } from 'react';
import { XIcon, SearchIcon, PlusIcon, EyeIcon, EyeOffIcon, XCircle, ListIcon, FileTextIcon, CalendarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { RichTextEditor } from './RichTextEditor';
import { Customer } from './CustomerCard';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (task: {
    taskId: string;
    title: string;
    customerId: string;
    description: string;
    status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending';
    category: 'Ladeboks' | 'Andet';
    chargerType?: 'Zaptec 1' | 'Zaptec 2' | 'Andet';
    taskType: 'Tilbudsopgave' | 'Regningsopgave';
    agreedPlanning?: string;
    agreedInstallation?: string;
    carDeliveryDate?: string;
    hours?: number;
    materialPrice?: number;
    hourlyRate?: number;
    quotePrice?: number;
    totalPrice?: number;
    invoiced: boolean;
    subTasks?: string;
    materials?: string[];
    workLog?: Array<{
      date: string;
      description: string;
    }>;
  }) => void;
  customers: Customer[];
  nextTaskId: string;
  initialCustomerId?: string; // NEW
}
export function AddTaskModal({
  isOpen,
  onClose,
  onAdd,
  customers,
  nextTaskId,
  initialCustomerId
}: AddTaskModalProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [category, setCategory] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setChargerPrices(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    if (isOpen) {
      loadPrices();
      if (initialCustomerId) {
        setCustomerId(initialCustomerId);
      }
    }
  }, [isOpen, initialCustomerId]);
  useEffect(() => {
    if (category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, category, chargerPrices]);
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const query = customerSearch.toLowerCase();
    return customers.filter(customer => customer.name.toLowerCase().includes(query) || customer.customerId.toLowerCase().includes(query) || customer.phone.toLowerCase().includes(query) || customer.email?.toLowerCase().includes(query));
  }, [customers, customerSearch]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = hours ? parseFloat(hours) : 0;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, hours]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      // NEW: Add single material price to total material price
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        const newTotalPrice = currentPrice + additionalPrice;
        setMaterialPrice(newTotalPrice.toString());
      }
      setMaterialInput('');
      setMaterialPriceInput(''); // Reset price input
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          const additionalPrice = parseFloat(bulkPrice);
          const newTotalPrice = currentPrice + additionalPrice;
          setMaterialPrice(newTotalPrice.toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      setWorkLog([...workLog, {
        date: workLogDate,
        description: workLogDescription.trim()
      }]);
      setWorkLogDescription('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => {
    setWorkLog(workLog.filter((_, i) => i !== index));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !customerId) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onAdd({
      taskId: nextTaskId,
      title,
      customerId,
      description,
      status,
      category,
      chargerType: category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined
    });
    // Reset form
    setTitle('');
    setCustomerId('');
    setCustomerSearch('');
    setDescription('');
    setStatus('in-progress');
    setCategory('Ladeboks');
    setChargerType('Zaptec 1');
    setTaskType('Tilbudsopgave');
    setAgreedPlanning('');
    setAgreedPlanningTime('09:00');
    setAgreedInstallation('');
    setAgreedInstallationTime('09:00');
    setCarDeliveryDate('');
    setHours('');
    setMaterialPrice('');
    setHourlyRate('');
    setQuotePrice('');
    setInvoiced('no');
    setSubTasks('');
    setMaterialInput('');
    setMaterialPriceInput(''); // NEW
    setBulkInput('');
    setBulkPrice('');
    setMaterials([]);
    setShowMaterials(false);
    setBulkMode(false);
    setWorkLogDate(new Date().toISOString().split('T')[0]); // NEW
    setWorkLogDescription(''); // NEW
    setWorkLog([]); // NEW
    setShowWorkLog(false); // NEW
    // Don't reset customerId if initialCustomerId is provided, or maybe do?
    // Better to reset everything and rely on useEffect to set it back when reopened with prop
    setCustomerId('');
    onClose();
  };
  return <AnimatePresence data-id="element-306">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-307" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-308">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 my-8" data-id="element-309">
              <div className="flex items-center justify-between mb-6" data-id="element-310">
                <div data-id="element-311">
                  <h2 className="text-2xl font-semibold text-stone-900" data-id="element-312">
                    Tilføj opgave
                  </h2>
                  <p className="text-sm text-stone-500 mt-1" data-id="element-313">
                    Opgave-ID:{' '}
                    <span className="font-medium text-teal-600" data-id="element-314">
                      {nextTaskId}
                    </span>
                  </p>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-315">
                  <XIcon className="w-6 h-6" data-id="element-316" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-317">
                {/* Basic Information */}
                <div data-id="element-318">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-319">
                    Grundlæggende information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-320">
                    <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required data-id="element-321" />

                    <div data-id="element-322">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-323">
                        Kunde <span className="text-red-500" data-id="element-324">*</span>
                      </label>
                      <div className="relative mb-2" data-id="element-325">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-326" />
                        <input type="text" placeholder="Søg efter kunde..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-327" />
                      </div>
                      <select value={customerId} onChange={e => setCustomerId(e.target.value)} required className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-328">
                        <option value="" data-id="element-329">Vælg en kunde</option>
                        {filteredCustomers.map(c => <option key={c.id} value={c.id} data-id="element-330">
                            {c.name} ({c.customerId})
                          </option>)}
                      </select>
                      {customerSearch && filteredCustomers.length === 0 && <p className="text-sm text-stone-500 mt-1" data-id="element-331">
                          Ingen kunder matcher søgningen
                        </p>}
                    </div>
                  </div>

                  <div className="mt-4" data-id="element-332">
                    <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} data-id="element-333" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-334">
                    <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                  value: 'planned',
                  label: 'Planlagt'
                }, {
                  value: 'pending',
                  label: 'Afventende'
                }, {
                  value: 'in-progress',
                  label: 'I gang'
                }, {
                  value: 'done',
                  label: 'Afsluttet'
                }]} data-id="element-335" />

                    <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-336" />
                  </div>
                </div>

                {/* Dates */}
                <div data-id="element-337">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-338">
                    Datoer
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-339">
                    <div data-id="element-340">
                      <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} data-id="element-341" />
                      {agreedPlanning && <div className="mt-2" data-id="element-342">
                          <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} data-id="element-343" />
                        </div>}
                    </div>

                    <div data-id="element-344">
                      <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} data-id="element-345" />
                      {agreedInstallation && <div className="mt-2" data-id="element-346">
                          <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} data-id="element-347" />
                        </div>}
                    </div>
                  </div>

                  {category === 'Ladeboks' && <div className="mt-4" data-id="element-348">
                      <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} data-id="element-349" />
                      <p className="text-xs text-stone-500 mt-1" data-id="element-350">
                        Angiv hvornår bilen leveres til kunden
                      </p>
                    </div>}
                </div>

                {/* Pricing */}
                <div data-id="element-351">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-352">
                    Priser og timer
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-353">
                    <Select label="Kategori" value={category} onChange={e => setCategory(e.target.value as 'Ladeboks' | 'Andet')} options={[{
                  value: 'Ladeboks',
                  label: 'Ladeboks'
                }, {
                  value: 'Andet',
                  label: 'Alm. Installation'
                }]} required data-id="element-354" />

                    <Select label="Opgavetype" value={taskType} onChange={e => setTaskType(e.target.value as 'Tilbudsopgave' | 'Regningsopgave')} options={[{
                  value: 'Tilbudsopgave',
                  label: 'Tilbudsopgave'
                }, {
                  value: 'Regningsopgave',
                  label: 'Regningsopgave'
                }]} required data-id="element-355" />
                  </div>

                  {taskType === 'Tilbudsopgave' ? <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-356">
                        <Input label="Tilbudspris (kr)" type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required data-id="element-357" />

                        <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-358" />
                      </div>

                      {category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-359">
                          Materialer pris udfyldes automatisk baseret på
                          ladeboks type
                        </p>}

                      <div className="mt-4" data-id="element-360">
                        <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" data-id="element-361" />
                      </div>

                      {/* Results for Tilbudsopgave */}
                      <div className="mt-4 space-y-3" data-id="element-362">
                        {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-363">
                            <div className="flex items-center justify-between" data-id="element-364">
                              <span className="text-sm font-medium text-teal-900" data-id="element-365">
                                Fortjeneste
                              </span>
                              <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-366">
                                {calculations.profit.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr
                              </span>
                            </div>
                          </div>}

                        {/* NEW: Real Hourly Rate */}
                        {calculations.realHourlyRate !== null && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-367">
                            <div className="flex items-center justify-between" data-id="element-368">
                              <span className="text-sm font-medium text-blue-900" data-id="element-369">
                                Reel timepris
                              </span>
                              <span className="text-lg font-bold text-blue-700" data-id="element-370">
                                {calculations.realHourlyRate.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr/time
                              </span>
                            </div>
                            <p className="text-xs text-blue-600 mt-1" data-id="element-371">
                              Fortjeneste / Timer brugt
                            </p>
                          </div>}
                      </div>
                    </> : <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-372">
                        <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required data-id="element-373" />

                        <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required data-id="element-374" />

                        <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-375" />
                      </div>

                      {category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-376">
                          Materialer pris udfyldes automatisk baseret på
                          ladeboks type
                        </p>}

                      {/* Results for Regningsopgave */}
                      <div className="mt-4 space-y-3" data-id="element-377">
                        <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg" data-id="element-378">
                          <div className="flex items-center justify-between" data-id="element-379">
                            <span className="text-sm font-medium text-stone-700" data-id="element-380">
                              Samlet pris
                            </span>
                            <span className="text-lg font-bold text-stone-900" data-id="element-381">
                              {calculations.totalPrice.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                              kr
                            </span>
                          </div>
                        </div>

                        {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-382">
                            <div className="flex items-center justify-between" data-id="element-383">
                              <span className="text-sm font-medium text-teal-900" data-id="element-384">
                                Fortjeneste
                              </span>
                              <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-385">
                                {calculations.profit.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr
                              </span>
                            </div>
                          </div>}

                        {/* NEW: Real Hourly Rate */}
                        {calculations.realHourlyRate !== null && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-386">
                            <div className="flex items-center justify-between" data-id="element-387">
                              <span className="text-sm font-medium text-blue-900" data-id="element-388">
                                Reel timepris
                              </span>
                              <span className="text-lg font-bold text-blue-700" data-id="element-389">
                                {calculations.realHourlyRate.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr/time
                              </span>
                            </div>
                            <p className="text-xs text-blue-600 mt-1" data-id="element-390">
                              Fortjeneste / Timer brugt
                            </p>
                          </div>}
                      </div>
                    </>}
                </div>

                {/* Materials Section */}
                <div data-id="element-391">
                  <div className="flex items-center justify-between mb-4" data-id="element-392">
                    <h3 className="text-lg font-semibold text-stone-900" data-id="element-393">
                      Materialer
                    </h3>
                    <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-394">
                      <ListIcon className="w-4 h-4" data-id="element-395" />
                      {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                    </button>
                  </div>

                  <AnimatePresence mode="wait" data-id="element-396">
                    {bulkMode ?
                // Bulk Mode (unchanged)
                <motion.div key="bulk" initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="space-y-3" data-id="element-397">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-398">
                          <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-399">
                            📦 Tilføj stor ordre
                          </p>
                          <p className="text-xs text-blue-700" data-id="element-400">
                            Skriv ét materiale per linje. Prisen lægges
                            automatisk til materialer pris.
                          </p>
                        </div>

                        <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-401" />

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-402">
                          <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-403">
                            💰 Pris for denne ordre (kr)
                          </label>
                          <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-404" />
                          {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-405">
                              <div className="flex items-center justify-between text-sm" data-id="element-406">
                                <span className="text-amber-700" data-id="element-407">
                                  Nuværende materialer pris:
                                </span>
                                <span className="font-medium text-amber-900" data-id="element-408">
                                  {parseFloat(materialPrice).toLocaleString('da-DK', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}{' '}
                                  kr
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm mt-1" data-id="element-409">
                                <span className="text-amber-700" data-id="element-410">
                                  Ordre pris:
                                </span>
                                <span className="font-medium text-amber-900" data-id="element-411">
                                  +
                                  {parseFloat(bulkPrice).toLocaleString('da-DK', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}{' '}
                                  kr
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-412">
                                <span className="font-semibold text-amber-900" data-id="element-413">
                                  Ny total:
                                </span>
                                <span className="font-bold text-amber-900" data-id="element-414">
                                  {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}{' '}
                                  kr
                                </span>
                              </div>
                            </div>}
                        </div>

                        <div className="flex gap-2" data-id="element-415">
                          <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2" disabled={!bulkInput.trim()} data-id="element-416">
                            <PlusIcon className="w-4 h-4" data-id="element-417" />
                            Tilføj alle materialer
                            {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                          </Button>
                          <Button type="button" onClick={() => {
                      setBulkInput('');
                      setBulkPrice('');
                      setBulkMode(false);
                    }} variant="secondary" data-id="element-418">
                            Annuller
                          </Button>
                        </div>
                      </motion.div> :
                // UPDATED: Single Mode with Price Input
                <motion.div key="single" initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="space-y-3" data-id="element-419">
                        <div className="flex gap-2" data-id="element-420">
                          <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMaterial();
                      }
                    }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-421" />
                          <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMaterial();
                      }
                    }} placeholder="Pris (kr)" className="w-32 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-422" />
                          <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center gap-2" disabled={!materialInput.trim()} data-id="element-423">
                            <PlusIcon className="w-4 h-4" data-id="element-424" />
                            Tilføj
                          </Button>
                        </div>

                        {/* NEW: Price Preview for Single Material */}
                        {materialPriceInput && materialPrice && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-425">
                            <div className="flex items-center justify-between text-sm" data-id="element-426">
                              <span className="text-green-700" data-id="element-427">
                                Nuværende total:
                              </span>
                              <span className="font-medium text-green-900" data-id="element-428">
                                {parseFloat(materialPrice).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm mt-1" data-id="element-429">
                              <span className="text-green-700" data-id="element-430">Tilføjes:</span>
                              <span className="font-medium text-green-900" data-id="element-431">
                                +
                                {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-432">
                              <span className="font-semibold text-green-900" data-id="element-433">
                                Ny total:
                              </span>
                              <span className="font-bold text-green-900" data-id="element-434">
                                {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                                kr
                              </span>
                            </div>
                          </motion.div>}
                      </motion.div>}
                  </AnimatePresence>

                  {/* Materials List */}
                  {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-435">
                      <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-436">
                        {showMaterials ? <>
                            <EyeOffIcon className="w-4 h-4" data-id="element-437" />
                            Skjul materialer ({materials.length})
                          </> : <>
                            <EyeIcon className="w-4 h-4" data-id="element-438" />
                            Se materialer ({materials.length})
                          </>}
                      </Button>

                      <AnimatePresence data-id="element-439">
                        {showMaterials && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-440">
                            <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-441">
                              {materials.map((material, index) => <motion.li key={index} initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} exit={{
                        opacity: 0,
                        x: -10
                      }} transition={{
                        delay: index * 0.02
                      }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-442">
                                  <span className="text-sm text-stone-900" data-id="element-443">
                                    {material}
                                  </span>
                                  <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-444">
                                    <XCircle className="w-4 h-4" data-id="element-445" />
                                  </button>
                                </motion.li>)}
                            </ul>
                          </motion.div>}
                      </AnimatePresence>
                    </div>}

                  {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-446">
                      Ingen materialer tilføjet endnu
                    </p>}
                </div>

                {/* NEW: Work Log Section */}
                <div data-id="element-447">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-448">
                    Arbejdslog
                  </h3>

                  <div className="space-y-3" data-id="element-449">
                    <div className="flex gap-2" data-id="element-450">
                      <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-451" />
                      <input type="text" value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddWorkLog();
                    }
                  }} placeholder="Beskriv hvad der blev lavet denne dag..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-452" />
                      <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2" disabled={!workLogDescription.trim()} data-id="element-453">
                        <PlusIcon className="w-4 h-4" data-id="element-454" />
                        Tilføj
                      </Button>
                    </div>
                  </div>

                  {/* Work Log List */}
                  {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-455">
                      <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-456">
                        {showWorkLog ? <>
                            <EyeOffIcon className="w-4 h-4" data-id="element-457" />
                            Skjul arbejdslog ({workLog.length})
                          </> : <>
                            <FileTextIcon className="w-4 h-4" data-id="element-458" />
                            Se arbejdslog ({workLog.length})
                          </>}
                      </Button>

                      <AnimatePresence data-id="element-459">
                        {showWorkLog && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-460">
                            <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-461">
                              {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => <motion.li key={index} initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} exit={{
                        opacity: 0,
                        x: -10
                      }} transition={{
                        delay: index * 0.02
                      }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-462">
                                    <div className="flex items-start justify-between gap-3" data-id="element-463">
                                      <div className="flex-1" data-id="element-464">
                                        <div className="flex items-center gap-2 mb-1" data-id="element-465">
                                          <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-466" />
                                          <span className="text-xs font-medium text-teal-700" data-id="element-467">
                                            {new Date(entry.date).toLocaleDateString('da-DK', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                          </span>
                                        </div>
                                        <p className="text-sm text-stone-900" data-id="element-468">
                                          {entry.description}
                                        </p>
                                      </div>
                                      <button type="button" onClick={() => handleRemoveWorkLog(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0" aria-label="Fjern log" data-id="element-469">
                                        <XCircle className="w-4 h-4" data-id="element-470" />
                                      </button>
                                    </div>
                                  </motion.li>)}
                            </ul>
                          </motion.div>}
                      </AnimatePresence>
                    </div>}

                  {workLog.length === 0 && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-471">
                      Ingen arbejdslog endnu
                    </p>}
                </div>

                {/* Vigtige bemærkninger */}
                <div data-id="element-472">
                  <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} data-id="element-473" />
                </div>

                <div className="flex gap-3 pt-2" data-id="element-474">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-475">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-476">
                    Tilføj opgave
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}